import { createContext } from "react";
import { ClientError, IPageInfo, IPagination, IThreadFilters } from "./api";
import { IFeedback, IStep, IThread } from "./types";
import { removeToken } from "./utils/token";
import { toQueryString } from "@/utils/hash";
import { DEFAULT_PAGINATION_PARAMS } from "@/utils/params";

type Payload = FormData | any;

class APIBase {
  constructor(
    public httpEndpoint: string,
    public type: "webapp" | "copilot" | "teams" | "slack" | "discord",
    public on401?: () => void,
    public onError?: (error: ClientError) => void
  ) {}

  buildEndpoint(path: string) {
    if (this.httpEndpoint.endsWith("/")) {
      // remove trailing slash on httpEndpoint
      return `${this.httpEndpoint.slice(0, -1)}${path}`;
    } else {
      return `${this.httpEndpoint}${path}`;
    }
  }

  checkToken(token: string) {
    const prefix = "Bearer ";
    if (token.startsWith(prefix)) {
      return token;
    } else {
      return prefix + token;
    }
  }

  async fetch(
    method: string,
    path: string,
    token?: string,
    data?: Payload,
    signal?: AbortSignal
  ): Promise<Response> {
    try {
      const headers: { Authorization?: string; "Content-Type"?: string } = {};
      if (token) headers["Authorization"] = this.checkToken(token); // Assuming token is a bearer token

      let body;

      if (data instanceof FormData) {
        body = data;
      } else {
        headers["Content-Type"] = "application/json";
        body = data ? JSON.stringify(data) : null;
      }

      const res = await fetch(this.buildEndpoint(path), {
        method,
        headers,
        signal,
        body,
      });

      if (!res.ok) {
        const body = await res.json();
        if (res.status === 401 && this.on401) {
          removeToken();
          this.on401();
        }
        throw new ClientError(body.message ?? res.statusText, body.error);
      }

      return res;
    } catch (error: any) {
      // if (error instanceof ClientError && this.onError) {
      if (this.onError) {
        this.onError(error);
      }
      console.error(error);
      throw error;
    }
  }

  async get(endpoint: string, data?: Payload, token?: string) {
    return await this.fetch("GET", endpoint + toQueryString(data), token);
  }

  async post(
    endpoint: string,
    data: Payload,
    token?: string,
    signal?: AbortSignal
  ) {
    return await this.fetch("POST", endpoint, token, data, signal);
  }

  async put(endpoint: string, data: Payload, token?: string) {
    return await this.fetch("PUT", endpoint, token, data);
  }

  async patch(endpoint: string, data: Payload, token?: string) {
    return await this.fetch("PATCH", endpoint, token, data);
  }

  async delete(endpoint: string, data: Payload, token?: string) {
    return await this.fetch("DELETE", endpoint, token, data);
  }
}

export class GenAiAPI extends APIBase {
  async headerAuth() {
    const res = await this.post(`/auth/header`, {});
    return res.json();
  }

  async passwordAuth(data: { username: string; password: string }) {
    const res = await this.post(`/api/auth/login`, data);
    return res.json();
  }

  async logout(accessToken: string) {
    const res = await this.get(`/api/auth/logout`, {}, accessToken);
    return res.json();
  }

  async sendMessage(
    question: string | any,
    threadId: string,
    action_type?: string,
    ref?: number | null,
    accessToken?: string,
    db_connection?: string,
    company_id?: string | number
  ): Promise<{
    success: boolean;
    data: { steps: IStep[]; thread_id: string };
    message: string;
  }> {
    const res = await this.post(
      `/api/chat/msg`,
      {
        question,
        thread_id: threadId,
        action_type: action_type,
        file_reference: ref ?? null,
        source_type: "",
        db_connection: db_connection,
        company_id: company_id,
      },
      accessToken
    );
    return res.json();
  }

  async setFeedback(
    feedback: IFeedback,
    accessToken?: string
  ): Promise<{ success: boolean; feedbackId: string }> {
    const res = await this.put(`/feedback`, { feedback }, accessToken);
    return res.json();
  }

  async deleteFeedback(
    feedbackId: string,
    accessToken?: string
  ): Promise<{ success: boolean }> {
    const res = await this.delete(`/feedback`, { feedbackId }, accessToken);
    return res.json();
  }

  async listThreads(
    pagination: IPagination,
    filter: IThreadFilters,
    accessToken?: string
  ): Promise<{
    data: {
      threads: IThread[];
      pageInfo: IPageInfo;
    };
  }> {
    const res = await this.get(
      `/api/chat/thread/list`,
      {
        search: filter.search || "",
        first: pagination.first,
        cursor: pagination.cursor || "",
      },
      accessToken
    );

    return res.json();
  }

  async deleteThread(threadId: string, accessToken?: string) {
    const res = await this.delete(
      `/api/chat/thread/${threadId}`,
      null,
      accessToken
    );

    return res.json();
  }

  async editThread(threadId: string, newName: string, accessToken?: string) {
    const data = {
      new_name: newName,
    };

    const res = await this.patch(
      `/api/chat/thread/${threadId}`,
      data,
      accessToken
    );

    return res.json();
  }

  async listFile(
    filters?: { search?: string; searchBy?: string },
    accessToken?: string
  ) {
    const res = await this.get(
      `/api/chat/file/list`,
      {
        ...DEFAULT_PAGINATION_PARAMS,
        search: filters?.search || "",
        search_by: filters?.searchBy || "",
      },
      accessToken
    );

    return res.json();
  }

  async previewFile(id: string | number, accessToken?: string) {
    return await this.get(`/api/chat/file/preview/${id}`, null, accessToken);
  }

  async downloadFile(id: string | number, accessToken?: string) {
    const res = await this.get(
      `/api/chat/file/download/${id}`,
      null,
      accessToken
    );

    const contentDisposition = res.headers.get("Content-Disposition");
    let filename = "downloaded_file"; // Default filename

    if (contentDisposition && contentDisposition.includes("attachment")) {
      const filenameMatch = contentDisposition.match(
        /filename[^;=\n]*=((['"]).*?\2|[^;\n]*)/
      );
      if (filenameMatch != null && filenameMatch[1]) {
        filename = filenameMatch[1].replace(/['"]/g, ""); // Remove any quotes
      }
    }

    return { blob: await res.blob(), filename };
  }

  async deleteFile(id: string | number, accessToken?: string) {
    const res = await this.delete(`/api/chat/file/${id}`, null, accessToken);

    return res.json();
  }

  uploadFile(
    file: File,
    onProgress: (progress: number) => void,
    sessionId: string,
    token?: string
  ) {
    const xhr = new XMLHttpRequest();

    const promise = new Promise<{ data: number }>((resolve, reject) => {
      const formData = new FormData();
      formData.append("file", file);

      xhr.open("POST", this.buildEndpoint(`/api/chat/file/upload`), true);

      if (token) {
        xhr.setRequestHeader("Authorization", this.checkToken(token));
      }

      // Track the progress of the upload
      xhr.upload.onprogress = function (event) {
        if (event.lengthComputable) {
          const percentage = (event.loaded / event.total) * 100;
          onProgress(percentage);
        }
      };

      xhr.onload = function () {
        if (xhr.status === 200) {
          const response = JSON.parse(xhr.responseText);
          resolve(response);
        } else {
          reject("Upload failed");
        }
      };

      xhr.onerror = function () {
        reject("Upload error");
        console.log(sessionId);
      };

      xhr.send(formData);
    });

    return { xhr, promise };
  }

  getElementUrl(id: string, sessionId: string) {
    const queryParams = `?session_id=${sessionId}`;
    return this.buildEndpoint(`/project/file/${id}${queryParams}`);
  }

  getLogoEndpoint(theme: string) {
    return this.buildEndpoint(`/logo?theme=${theme}`);
  }

  getOAuthEndpoint(provider: string) {
    return this.buildEndpoint(`/auth/oauth/${provider}`);
  }

  async getAccountReport(id: string | number, accessToken?: string) {
    const res = await this.get(
      `/api/account/select-company/${id}`,
      null,
      accessToken
    );
    return res.json();
  }

  async updateAccountReport(
    data: { thread_id: string; company_id: string; additional_info: string },
    accessToken?: string
  ) {
    const res = await this.post(
      `/api/account/update-reports`,
      data,
      accessToken
    );
    return res.json();
  }

  async createConnectionString(
    data: { name: string; connection_string: string; database_type: string },
    accessToken?: string
  ) {
    const res = await this.post(
      `/api/chat/connection_strings`,
      data,
      accessToken
    );
    return res.json();
  }

  async activateConnectionString(
    data: { connection_id: number },
    accessToken?: string
  ) {
    const res = await this.post(`/api/connection/activate`, data, accessToken);
    return res.json();
  }

  async getConnectionStrings(accessToken?: string) {
    const res = await this.get(`/api/connection/available`, null, accessToken);
    return res.json();
  }

  async editConnectionString(
    connectionId: number,
    data: { name: string; connection_string: string; database_type: string },
    accessToken?: string
  ) {
    const res = await this.put(
      `/api/chat/connection_strings/${connectionId}`,
      data,
      accessToken
    );
    return res.json();
  }

  async deleteConnectionString(
    connectionId: string | number,
    accessToken?: string
  ) {
    const res = await this.delete(
      `/api/chat/connection_strings/${connectionId}`,
      null,
      accessToken
    );

    return res.json();
  }

  async getNotifications(accessToken?: string) {
    const res = await this.get(`/api/notification/`, {}, accessToken);
    return res.json();
  }

  async markNotificationAsRead(notifId: string, accessToken?: string) {
    const res = await this.patch(
      `/api/notification/${notifId}/read`,
      {},
      accessToken
    );

    return res.json();
  }

  async markAllNotificationsAsRead(accessToken?: string) {
    const res = await this.patch(
      `/api/notification/mark-all-read`,
      {},
      accessToken
    );

    return res.json();
  }

  async markAllNotificationsAsUnread(accessToken?: string) {
    const res = await this.patch(
      `/api/notification/mark-all-unread`,
      {},
      accessToken
    );

    return res.json();
  }

  // User Management
  // General Information & Profile
  async getAdminProfileInfo(accessToken?: string) {
    const res = await this.get(`/api/profile/`, {}, accessToken);
    return res.json();
  }

  async updateAdminProfileInfo(
    data: {
      first_name: string;
      last_name: string;
      email: string;
      phone_number: string;
    },
    accessToken?: string
  ) {
    const res = await this.put(`/api/profile/info`, data, accessToken);
    return res.json();
  }

  async changeUserPassword(
    data: {
      new_password: string;
    },
    accessToken?: string
  ) {
    const res = await this.put(`/api/profile/password`, data, accessToken);
    return res.json();
  }

  async getOrganizationInfo(accessToken?: string) {
    const res = await this.get(`/api/profile/organization`, {}, accessToken);
    return res.json();
  }

  async editOrganizationName(
    data: {
      organization_name: string;
    },
    accessToken?: string
  ) {
    const res = await this.put(`/api/profile/organization`, data, accessToken);
    return res.json();
  }

  // Members
  async getUserList(accessToken?: string) {
    const res = await this.get(`/api/user/all`, {}, accessToken);
    return res.json();
  }

  async editUserRole(
    data: {
      id: number;
      role: string;
    },
    accessToken?: string
  ) {
    const res = await this.post(
      `/api/user/assign-role/${data.id}`,
      null,
      accessToken
    );

    return res.json();
  }

  async deleteUser(id: number | undefined, accessToken?: string) {
    const res = await this.delete(
      `/api/user/delete-user/${id}`,
      null,
      accessToken
    );

    return res.json();
  }

  // DB Connections
  async getConnectionsList(accessToken?: string) {
    const res = await this.get(`/api/connection/list`, {}, accessToken);
    return res.json();
  }

  async createConnection(
    data: {
      database_driver: string;
      host: string;
      port: number;
      db_name: string;
      username: string;
      password: string;
    },
    accessToken?: string
  ) {
    const res = await this.post(`/api/connection/`, data, accessToken);
    return res.json();
  }

  async editConnection(
    data: {
      id: number;
      database_driver: string;
      host: string;
      port: number;
      db_name: string;
      username: string;
      password: string;
    },
    accessToken?: string
  ) {
    const res = await this.put(`/api/connection/`, data, accessToken);
    return res.json();
  }

  async testConnection(
    data: {
      database_driver: string;
      host: string;
      port: number;
      db_name: string;
      username: string;
      password: string;
    },
    accessToken?: string
  ) {
    const res = await this.post(`/api/connection/test`, data, accessToken);
    return res.json();
  }

  async deleteConnection(id: number | undefined, accessToken?: string) {
    const res = await this.delete(
      `/api/connection/?id=${id}`,
      null,
      accessToken
    );

    return res.json();
  }

  async getConnectionsByProjectId(
    project_id: number | undefined,
    accessToken?: string
  ) {
    const res = await this.get(
      `/api/connection/project?project_id=${project_id}`,
      null,
      accessToken
    );

    return res.json();
  }

  async getUnassignedConnections(
    project_id: number | undefined,
    accessToken?: string
  ) {
    const res = await this.get(
      `/api/connection/unassigned?project_id=${project_id}`,
      null,
      accessToken
    );

    return res.json();
  }

  async assignConnectionToProject(
    data: { project_id: number; connection_id: number },
    accessToken?: string
  ) {
    const res = await this.post(`/api/connection/assign`, data, accessToken);
    return res.json();
  }

  async removeConnectionFromProject(
    data: { project_id: number; connection_id: number },
    accessToken?: string
  ) {
    const res = await this.post(`/api/connection/remove`, data, accessToken);
    return res.json();
  }

  // Project
  async getProjectList(accessToken?: string) {
    const res = await this.get(`/api/project/list`, {}, accessToken);
    return res.json();
  }

  async getAllUsers(accessToken?: string) {
    const res = await this.get(`/api/project/users`, {}, accessToken);
    return res.json();
  }

  async getProjectById(project_id: number | undefined, accessToken?: string) {
    const res = await this.get(
      `/api/project/detail?project_id=${project_id}`,
      null,
      accessToken
    );

    return res.json();
  }

  async createProject(data: { name: string }, accessToken?: string) {
    const res = await this.post(`/api/project/`, data, accessToken);
    return res.json();
  }

  async editProject(
    data: { project_id: number | undefined; new_name: string },
    accessToken?: string
  ) {
    const res = await this.post(`/api/project/edit-name`, data, accessToken);
    return res.json();
  }

  async deleteProject(project_id: number | undefined, accessToken?: string) {
    const res = await this.delete(
      `/api/project/?project_id=${project_id}`,
      null,
      accessToken
    );

    return res.json();
  }

  async addMemberToProject(
    data: {
      project_id: number | undefined;
      user_id: string | number;
      role: string;
    },
    accessToken?: string
  ) {
    const res = await this.post(`/api/project/add-member`, data, accessToken);
    return res.json();
  }

  async editMemberRole(
    data: {
      project_id: number | undefined;
      user_id: string | number;
      new_role: string;
    },
    accessToken?: string
  ) {
    const res = await this.post(`/api/project/edit-role`, data, accessToken);
    return res.json();
  }

  async removeMemberFromProject(
    data: { project_id: number | undefined; user_id: string | number },
    accessToken?: string
  ) {
    const res = await this.post(
      `/api/project/remove-member`,
      data,
      accessToken
    );

    return res.json();
  }

  // LiuGong
  async getAllAccounts(accessToken?: string) {
    const res = await this.get(`/api/account/`, {}, accessToken);
    return res.json();
  }

  async getAllAccountsNoPagination(accessToken?: string) {
    const res = await this.get(`/api/account/companies`, {}, accessToken);
    return res.json();
  }

  async getAllCompanies(accessToken?: string) {
    const res = await this.get(`/api/account/emiten-codes`, {}, accessToken);
    return res.json();
  }

  async documentCheck(id: string | number, accessToken?: string) {
    const res = await this.get(
      `/api/account/document-check?company_id=${id}`,
      null,
      accessToken
    );
    return res.json();
  }

  async getDocument(id: string | number, accessToken?: string) {
    const res = await this.get(
      `/api/account/download-report/${id}`,
      {},
      accessToken
    );

    // detect if response is JSON (error case) or file (success case)
    const contentType = res.headers.get("content-type");

    if (contentType && contentType.includes("application/json")) {
      // probably an error message
      return await res.json();
    } else {
      // file download
      const blob = await res.blob();
      return blob;
    }
  }

  async getDocumentUrl(id: string | number, accessToken?: string) {
    const res = await this.get(
      `/api/account/report-url/${id}`,
      {},
      accessToken
    );

    return res.json();
  }

  async createAccount(
    data: {
      company_name: string;
      company_id: string;
      kode: string;
    },
    accessToken?: string
  ) {
    const res = await this.post(`/api/account`, data, accessToken);
    return res.json();
  }

  async editAccount(
    id: string,
    data: {
      company_name?: string;
      application_form_filename?: string;
      application_form_url?: string;
      company_profile_filename?: string;
      company_profile_url?: string;
      financial_statement_filename?: string;
      financial_statement_url?: string;
      other_file_filename?: string;
      other_file_url?: string;
    },
    accessToken?: string
  ) {
    const res = await this.put(`/api/account/${id}`, data, accessToken);
    return res.json();
  }

  async deleteAccount(id: string | number, accessToken?: string) {
    const res = await this.delete(`/api/account/${id}`, null, accessToken);
    return res.json();
  }

  async getAccount(id: string | number, accessToken?: string) {
    const res = await this.get(
      `/api/account/form-options?id=${id}`,
      null,
      accessToken
    );
    return res.json();
  }

  async getCompany(id: string | number, accessToken?: string) {
    const res = await this.get(
      `/api/account/form-options?kode=${id}`,
      null,
      accessToken
    );
    return res.json();
  }

  uploadAccountFile(
    file: File,
    onProgress: (progress: number) => void,
    fileType: string,
    sessionId: string,
    token?: string,
    companyId?: string
  ) {
    const xhr = new XMLHttpRequest();

    const promise = new Promise<{ data: any }>((resolve, reject) => {
      const formData = new FormData();
      formData.append("file", file);
      if (companyId) formData.append("company_id", companyId);
      formData.append("file_category", fileType);

      xhr.open("POST", this.buildEndpoint(`/api/account/upload`), true);

      if (token) {
        xhr.setRequestHeader("Authorization", this.checkToken(token));
      }

      // Track upload progress
      xhr.upload.onprogress = function (event) {
        if (event.lengthComputable) {
          const percentage = (event.loaded / event.total) * 100;
          onProgress(percentage);
        }
      };

      xhr.onload = function () {
        if (xhr.status >= 200 && xhr.status < 300) {
          try {
            const response = JSON.parse(xhr.responseText);
            resolve(response);
          } catch (e) {
            reject("Invalid JSON response");
          }
        } else {
          reject(`Upload failed with status ${xhr.status}`);
        }
      };

      xhr.onerror = function () {
        reject("Upload error");
        console.log(sessionId);
      };

      xhr.send(formData);
    });

    return { xhr, promise };
  }

  async deleteAccountFile(
    data: {
      company_id: string;
      file_category: string;
      identifier: string;
    },
    accessToken?: string
  ) {
    const res = await this.delete(`/api/account/document`, data, accessToken);
    return res.json();
  }
}

const defaultGenAiContext = undefined;

const GenAiContext = createContext<GenAiAPI>(
  new GenAiAPI("http://localhost:8000", "webapp")
);

export { GenAiContext, defaultGenAiContext };
